/**
 * Where WL-5 invoices show up outside their own pages:
 *
 *   WlTicketInvoiceLink   on a customer-approved field ticket — "Not invoiced
 *                         yet" with a link to New invoice, or the invoice
 *                         that holds it. Read from the ticket itself: the API
 *                         names its live invoice (invoiceId / invoiceNumber /
 *                         invoiceStatus, fieldiq-api#1359). A customer-approved
 *                         ticket with no live invoice is exactly what
 *                         GET /invoices/billable lists, so null means ready.
 *   WlJobInvoicesSection  on a job's Tickets tab — the invoices whose job is
 *                         this one. An invoice billing tickets from several
 *                         jobs carries no job, so it is not listed here, and
 *                         the panel says that too.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useWlInvoices } from '../../../hooks/useWlInvoices';
import { fmtCurrency } from '../../../lib/display-labels';
import { wlDay } from '../../../lib/wireline-slickline-jobs';
import { wlInvoiceStatusSkin } from '../../../lib/wireline-slickline-invoices';
import type { WlInvoiceRow } from '../../../types/wireline-slickline-invoices';
import type { WlTicket } from '../../../types/wireline-slickline-tickets';
import { useOpenRecord } from '../../downhole/recordPage';
import { WlBadge, WlEmpty, WlPanel, WlRegister, type WlColumn } from '../WlBits';
import { WL_NEW_INVOICE_PATH } from '../../../lib/wireline-slickline-invoices';

type TicketInvoiceRef = Pick<WlTicket, 'id' | 'invoiceId' | 'invoiceNumber' | 'invoiceStatus' | 'invoiceStatusLabel'>;

export function WlTicketInvoiceLink({ ticket }: { ticket: TicketInvoiceRef }) {
  let body: React.ReactNode;
  if (ticket.invoiceId === undefined) {
    // An API without the field: absent is unknown, never "not invoiced".
    body = 'Whether this ticket is invoiced could not be read.';
  } else if (ticket.invoiceId) {
    body = (
      <>
        On invoice{' '}
        <Link className="font-mono font-bold underline" to={`/wireline-slickline/invoices/${ticket.invoiceId}`} data-testid="wl-ticket-invoice-holder">
          {ticket.invoiceNumber || '—'}
        </Link>{' '}
        ({ticket.invoiceStatusLabel || ticket.invoiceStatus || '—'}).
      </>
    );
  } else {
    body = (
      <>
        Not invoiced yet.{' '}
        <Link className="font-bold underline" to={`${WL_NEW_INVOICE_PATH}?ticket=${ticket.id}`} data-testid="wl-ticket-invoice-ready">
          Put it on a new invoice
        </Link>
        .
      </>
    );
  }

  return (
    <div role="note" data-testid="wl-ticket-invoice" className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
      <p>{body}</p>
    </div>
  );
}

const jobInvoiceColumns: WlColumn<WlInvoiceRow>[] = [
  { key: 'number', header: 'Invoice', render: (r) => <span className="font-mono font-extrabold text-white">{r.invoiceNumber || '—'}</span> },
  { key: 'status', header: 'Status', render: (r) => <WlBadge skin={wlInvoiceStatusSkin(r.status)} value={r.status} /> },
  { key: 'date', header: 'Invoice date', render: (r) => wlDay(r.invoiceDate) },
  { key: 'due', header: 'Due', render: (r) => wlDay(r.dueDate) },
  { key: 'total', header: 'Total', align: 'right', render: (r) => <span className="font-mono">{fmtCurrency(r.total, r.currency)}</span> },
];

export function WlJobInvoicesSection({ jobId }: { jobId: string }) {
  const openRecord = useOpenRecord();
  const query = useWlInvoices({ job_id: jobId });
  const rows = query.data?.data ?? [];
  return (
    <WlPanel
      eyebrow="Billed"
      title="Invoices"
      testId="wl-job-invoices"
      footer="An invoice billing tickets from several jobs is not listed here: find it on the Invoices register."
    >
      <WlRegister
        query={query}
        rows={rows}
        columns={jobInvoiceColumns}
        rail={(r) => wlInvoiceStatusSkin(r.status).rail}
        onOpen={(r) => openRecord(`/wireline-slickline/invoices/${r.id}`)}
        subject="this job's invoices"
        testId="wl-job-invoice"
        minWidth="min-w-[640px]"
        empty={<WlEmpty>No invoice bills this job yet.</WlEmpty>}
        card={(r) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-mono text-sm font-extrabold text-white">{r.invoiceNumber || '—'}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">Dated {wlDay(r.invoiceDate)} · {fmtCurrency(r.total, r.currency)}</p>
            </div>
            <WlBadge skin={wlInvoiceStatusSkin(r.status)} value={r.status} />
          </div>
        )}
      />
    </WlPanel>
  );
}
